import type { ExtensionParams, Parameters } from '@atlaskit/editor-common/extensions';

import { CalendarView } from './__types__/FullCalendar.ts';

export type MacroLegendPosition = 'bottom' | 'right' | 'none';

export type TeamCalendarMacroOptions = {
	subCalendarId?: string;
	legendPosition: MacroLegendPosition;
	calendarView?: CalendarView;
	hideWeekends: boolean;
};

export type TeamCalendarMacroDimensions = {
	width: string;
	height?: string;
};

export type MacroParams = {
	id?: { value?: string };
	defaultView?: { value?: string };
	width?: { value?: string };
	height?: { value?: string };
	/** Position of the subcalendar legend */
	showLegend?: { value?: string };
	/** 'true' to hide weekend columns from the calendar grid. */
	hideWeekends?: { value?: string };
};

/**
 * Maps the legacy macro `defaultView` parameter value to a FullCalendar view.
 *
 * Legacy values: 'month', 'week', 'list', 'upcoming', 'timeline'.
 * 'upcoming' is treated as 'list' (matching legacy behavior).
 */
const MACRO_VIEW_TO_CALENDAR_VIEW: Record<string, CalendarView> = {
	month: CalendarView.dayGridMonth,
	week: CalendarView.timeGridWeek,
	list: CalendarView.listMonth,
	upcoming: CalendarView.listMonth,
	timeline: CalendarView.timeline,
};

const VALID_LEGEND_POSITIONS = new Set<string>(['bottom', 'right', 'none']);
const DEFAULT_MACRO_WIDTH = '100%';
const DEFAULT_MACRO_HEIGHT = '720px';
const DEFAULT_MACRO_TIMELINE_HEIGHT = '500px';
const DEFAULT_LIST_VIEW_HEIGHT = '360px';
const MACRO_TOOLBAR_RESERVED_HEIGHT = '32px';
const MACRO_BOTTOM_LEGEND_RESERVED_HEIGHT = '140px';

/**
 * Extracts macro parameters from the ADF extension node.
 *
 * Note: generic `Parameters` type does not model legacy macroParams
 */
export function getMacroParams(
	extensionNode?: ExtensionParams<Parameters>,
): MacroParams | undefined {
	const parameters = extensionNode?.parameters as { macroParams?: MacroParams } | undefined;
	return parameters?.macroParams;
}

/**
 * Normalizes a dimension value to a valid CSS string.
 * If the value is purely numeric, appends 'px'. Otherwise returns as-is
 * (e.g. '100%', '50vw'). When a fallback is provided, returns it if value is empty.
 */
function toCssDimension(value: string | undefined, fallback: string): string {
	if (!value) {
		return fallback;
	}
	return /^\d+$/.test(value) ? `${value}px` : value;
}

function getMacroWidth(width: string | undefined): string {
	return toCssDimension(width, DEFAULT_MACRO_WIDTH);
}

function getMacroLegendPosition(
	legendPosition?: string,
	calendarView?: CalendarView,
): MacroLegendPosition {
	// Legacy list-style macro views do not render the calendar legend; an undefined view
	// still uses the default legend position.
	if (calendarView === CalendarView.listMonth) {
		return 'none';
	}

	return legendPosition && VALID_LEGEND_POSITIONS.has(legendPosition)
		? (legendPosition as MacroLegendPosition)
		: 'bottom';
}

function getHideWeekends(calendarView: CalendarView | undefined, hideWeekends?: string): boolean {
	return (
		hideWeekends === 'true' &&
		// Missing defaultView renders as month in TeamCalendarContainer
		(calendarView === undefined ||
			calendarView === CalendarView.dayGridMonth ||
			calendarView === CalendarView.timeGridWeek)
	);
}

function getMacroHeight(
	calendarView: CalendarView | undefined,
	savedHeight: string | undefined,
	legendPosition: MacroLegendPosition,
): string {
	if (calendarView === CalendarView.listMonth) {
		return DEFAULT_LIST_VIEW_HEIGHT;
	}

	if (calendarView !== CalendarView.timeline) {
		return DEFAULT_MACRO_HEIGHT;
	}

	const height = toCssDimension(savedHeight, DEFAULT_MACRO_TIMELINE_HEIGHT);

	if (legendPosition === 'bottom') {
		return `calc(${height} + ${MACRO_TOOLBAR_RESERVED_HEIGHT} + ${MACRO_BOTTOM_LEGEND_RESERVED_HEIGHT})`;
	}

	return `calc(${height} + ${MACRO_TOOLBAR_RESERVED_HEIGHT})`;
}

export function getMacroOptions(macroParams?: MacroParams): TeamCalendarMacroOptions {
	const savedDefaultView = macroParams?.defaultView?.value;
	const calendarView = savedDefaultView ? MACRO_VIEW_TO_CALENDAR_VIEW[savedDefaultView] : undefined;
	const legendPosition = macroParams?.showLegend?.value;

	return {
		subCalendarId: macroParams?.id?.value,
		calendarView,
		legendPosition: getMacroLegendPosition(legendPosition, calendarView),
		hideWeekends: getHideWeekends(calendarView, macroParams?.hideWeekends?.value),
	};
}

export function getMacroDimensions(
	macroParams: MacroParams | undefined,
	macroOptions: TeamCalendarMacroOptions,
): TeamCalendarMacroDimensions {
	return {
		width: getMacroWidth(macroParams?.width?.value),
		height: getMacroHeight(
			macroOptions.calendarView,
			macroParams?.height?.value,
			macroOptions.legendPosition,
		),
	};
}
